import {useState} from "react";
import {Button} from "../../../basic/button.tsx";
import {UserPlus} from "lucide-react";
import {InviteTeamMemberDialog} from "../../../dialogs/user/InviteTeamMemberDialog";
import {ManageUsersDialog} from "../../../dialogs/user/ManageUsersDialog";
import {RemoveMemberAlertDialog} from "../../../dialogs/user/RemoveMemberAlertDialog.tsx";
import {toast} from "sonner";
import {logger} from "../../../../utils/logger.ts";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {PaginationControls} from "../../../shared/Pagination.tsx";
import {SearchInput} from "../../../shared/search/SearchInput.tsx";
import {MemberTable} from "./MemberTable.tsx";
import {ArchivedMemberTable} from "../../../shared/tables/ArchivedMemberTable.tsx";
import {useGetPortalTeamQuery, useGetPortalTeamArchivedMembersQuery, useRemoveTeamMemberMutation, VwpTeamMember} from "../../../../store/generated/api.ts";
import {Card} from "../../../basic/card.tsx";
import {getDisplayName} from "../../../../utils/nameUtils";
import {Tabs, TabsList, TabsTrigger, TabsContent} from "../../../basic/tabs.tsx";

export function TeamMembersView() {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [activeTab, setActiveTab] = useState("active");
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [manageLicensesDialogOpen, setManageLicensesDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState<VwpTeamMember | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const [activePageNum, setActivePageNum] = useState(0);
    const [activePageSize, setActivePageSize] = useState(10);

    const [archivedPageNum, setArchivedPageNum] = useState(0);
    const [archivedPageSize, setArchivedPageSize] = useState(10);

    const {
        data: teamData,
        isLoading: isLoadingActive,
        error: activeError,
        refetch: refetchActive
    } = useGetPortalTeamQuery({
        page: activePageNum,
        size: activePageSize,
        search: searchTerm || undefined
    });

    const {
        data: archivedData,
        isLoading: isLoadingArchived,
        error: archivedError,
        refetch: refetchArchived
    } = useGetPortalTeamArchivedMembersQuery(
        {
            page: archivedPageNum,
            size: archivedPageSize
        },
        {
            skip: activeTab !== "archived"
        }
    );

    const [removeTeamMember, {isLoading: isRemoving}] = useRemoveTeamMemberMutation();

    const activeMembers = teamData?.teamMembers?.members || [];
    const activePagination = teamData?.teamMembers?.pagination;
    const team = teamData?.team;

    const archivedMembers = archivedData?.members || [];
    const archivedPagination = archivedData?.pagination;

    // Transform license data for dialogs
    const availableLicenses = team?.licenses?.map(license => ({
        id: parseInt(license.id), // Convert string id to number for dialog compatibility
        product: license.product,
        available: license.quantity - license.assigned
    })) || [];

    const isLoading = activeTab === "active" ? isLoadingActive : isLoadingArchived;
    const error = activeTab === "active" ? activeError : archivedError;
    const refetch = activeTab === "active" ? refetchActive : refetchArchived;

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setActivePageNum(0);
        setArchivedPageNum(0);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('team.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('team.subtitle')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <p>{t('common.loading')}</p>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('team.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('team.subtitle')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <p className="text-red-500">{t('common.error')}</p>
                                <Button
                                    variant="outline"
                                    onClick={() => refetch()}
                                    className="mt-2"
                                >
                                    {t('common.retry')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    const handleRemoveMember = (member: VwpTeamMember) => {
        setSelectedMember(member);
        setRemoveDialogOpen(true);
    };

    const handleConfirmRemove = async () => {
        if (!selectedMember?.email) {
            logger.error("No member email available for removal");
            showErrorToast({message: t('common.error')});
            return;
        }

        try {
            logger.info(`Removing team member: ${getDisplayName(selectedMember)} (${selectedMember.email})`);

            await removeTeamMember({userEmail: selectedMember.email}).unwrap();

            toast.success(t('team.memberRemoved', {name: getDisplayName(selectedMember)}));
            setRemoveDialogOpen(false);
            setSelectedMember(null);

            // Cache invalidation is handled automatically by RTK Query
        } catch (error) {
            logger.error("Failed to remove team member:", error);
            handleApiError(error, {fallbackMessage: t('team.memberRemoveError')});
        }
    };

    const handleManageLicenses = (member: VwpTeamMember) => {
        setSelectedMember(member);
        setManageLicensesDialogOpen(true);
    };

    const handleActivePageChange = (page: number) => {
        setActivePageNum(page - 1);
    };

    const handleActivePageSizeChange = (newPageSize: number) => {
        setActivePageSize(newPageSize);
        setActivePageNum(0);
    };

    const handleArchivedPageChange = (page: number) => {
        setArchivedPageNum(page - 1);
    };

    const handleArchivedPageSizeChange = (newPageSize: number) => {
        setArchivedPageSize(newPageSize);
        setArchivedPageNum(0);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setActivePageNum(0);
        setArchivedPageNum(0);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1>{t('team.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('team.subtitle')}</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => setInviteDialogOpen(true)}>
                        <UserPlus className="h-4 w-4 mr-2"/>
                        {t('team.addMember')}
                    </Button>
                </div>
            </div>

            <SearchInput
                placeholder={t('team.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
            />

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList>
                    <TabsTrigger value="active">
                        {t('team.active')}
                    </TabsTrigger>
                    <TabsTrigger value="archived">
                        {t('team.archived')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="space-y-4">
                    {activeMembers.length === 0 ? (
                        <Card className="shadow-sm border-border/50">
                            <div className="p-6">
                                <div className="flex items-center justify-center h-32">
                                    <p className="text-muted-foreground">{t('team.noActiveMembers')}</p>
                                </div>
                            </div>
                        </Card>
                    ) : (
                        <>
                            <MemberTable
                                paginatedData={activeMembers}
                                onRemoveMember={handleRemoveMember}
                                onManageLicenses={handleManageLicenses}
                            />

                            {activePagination && (
                                <PaginationControls
                                    currentPage={activePagination.page + 1}
                                    totalPages={activePagination.totalPages}
                                    pageSize={activePagination.size}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    paginationInfo={{
                                        start: activePagination.page * activePagination.size + 1,
                                        end: Math.min((activePagination.page + 1) * activePagination.size, activePagination.total),
                                        total: activePagination.total
                                    }}
                                    onPageChange={handleActivePageChange}
                                    onPageSizeChange={handleActivePageSizeChange}
                                    rowsPerPageLabel={t('common.rowsPerPage')}
                                    previousLabel={t('common.action.previous')}
                                    nextLabel={t('common.action.next')}
                                />
                            )}
                        </>
                    )}
                </TabsContent>

                <TabsContent value="archived" className="space-y-4">
                    {isLoading ? (
                        <Card className="shadow-sm border-border/50">
                            <div className="p-6">
                                <div className="flex items-center justify-center h-32">
                                    <p>{t('common.loading')}</p>
                                </div>
                            </div>
                        </Card>
                    ) : archivedError ? (
                        <Card className="shadow-sm border-border/50">
                            <div className="p-6">
                                <div className="flex items-center justify-center h-32">
                                    <div className="text-center">
                                        <p className="text-red-500">{t('common.error')}</p>
                                        <Button
                                            variant="outline"
                                            onClick={() => refetchArchived()}
                                            className="mt-2"
                                        >
                                            {t('common.retry')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ) : archivedMembers.length === 0 ? (
                        <Card className="shadow-sm border-border/50">
                            <div className="p-6">
                                <div className="flex items-center justify-center h-32">
                                    <p className="text-muted-foreground">{t('team.noArchivedMembers')}</p>
                                </div>
                            </div>
                        </Card>
                    ) : (
                        <>
                            <ArchivedMemberTable paginatedData={archivedMembers} />

                            {archivedPagination && (
                                <PaginationControls
                                    currentPage={archivedPagination.page + 1}
                                    totalPages={archivedPagination.totalPages}
                                    pageSize={archivedPagination.size}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    paginationInfo={{
                                        start: archivedPagination.page * archivedPagination.size + 1,
                                        end: Math.min((archivedPagination.page + 1) * archivedPagination.size, archivedPagination.total),
                                        total: archivedPagination.total
                                    }}
                                    onPageChange={handleArchivedPageChange}
                                    onPageSizeChange={handleArchivedPageSizeChange}
                                    rowsPerPageLabel={t('common.rowsPerPage')}
                                    previousLabel={t('common.action.previous')}
                                    nextLabel={t('common.action.next')}
                                />
                            )}
                        </>
                    )}
                </TabsContent>
            </Tabs>

            <InviteTeamMemberDialog
                open={inviteDialogOpen}
                onOpenChange={setInviteDialogOpen}
            />

            <ManageUsersDialog
                open={manageLicensesDialogOpen}
                onOpenChange={setManageLicensesDialogOpen}
                selectedMember={selectedMember ? {
                    id: selectedMember.id,
                    name: getDisplayName(selectedMember),
                    email: selectedMember.email,
                    role: selectedMember.role,
                    licenses: selectedMember.licenses // Pass actual license objects
                } : null}
            />

            <RemoveMemberAlertDialog
                open={removeDialogOpen}
                onOpenChange={setRemoveDialogOpen}
                onConfirm={handleConfirmRemove}
                memberName={selectedMember ? getDisplayName(selectedMember) : undefined}
                memberLicenses={selectedMember?.licenses?.map(license => license.name) || []}
                isRemoving={isRemoving}
            />
        </div>
    );
}